import React, { Suspense } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from './store'

import Breadcrumbs from 'breadcrumbs/Breadcrumbs'
import { customerTypeConfig } from 'config/customersTypes'
import { shiftEnabled } from 'config/shift'
import Permissions from './model/Permissions'
import {
  canManageSupplyChecklist,
  canViewReports,
  isInterpreter,
  isSupplyManager,
} from './utils/enums'
import { ButtonBarProvider } from 'page-header/ButtonBarProvider'
import { Spinner, Toaster } from '@hospitalrun/components'
// Not lazy: it has to work on a stale build, whose other chunks may already be gone from the server.
import ForceUpdate from 'forceUpdate/ForceUpdate'
const ViewAppointment = React.lazy(() => import('patients/appointments/view/ViewAppointment'))
const NewPatient = React.lazy(() => import('./patients/new/NewPatient'))
const EditPatient = React.lazy(() => import('./patients/edit/EditPatient'))
const ViewPatient = React.lazy(() => import('./patients/view/ViewPatient/ViewPatient'))
const Navbar = React.lazy(() => import('./components/Navbar'))
const PrivateRoute = React.lazy(() => import('./components/PrivateRoute'))
const Login = React.lazy(() => import('login/Login'))
const Appointments = React.lazy(() => import('scheduling/appointments/Appointments'))
const Clinics = React.lazy(() => import('clinics/Clinincs'))
const NewReferral = React.lazy(() => import('patients/referral/NewReferral'))
const NewApproval = React.lazy(() => import('patients/approvals/NewApproval'))
const Settings = React.lazy(() => import('settings/Settings'))
const MyProfile = React.lazy(() => import('user/MyProfile'))
const Users = React.lazy(() => import('user/Users'))
const ViewUser = React.lazy(() => import('user/ViewUser'))
const NewUser = React.lazy(() => import('user/NewUser'))
const EditUser = React.lazy(() => import('user/EditUser'))
const Groups = React.lazy(() => import('groups/groups'))
const DeviceIncidents = React.lazy(() => import('settings/deviceIncidents/ListDeviceIncidents'))
const NewGroup = React.lazy(() => import('groups/NewGroup'))
const EditGroup = React.lazy(() => import('groups/EditGroup'))
const ViewGroupTabs = React.lazy(() => import('groups/ViewGroup'))
const ButtonToolBar = React.lazy(() => import('page-header/ButtonToolBar'))
const UserActivation = React.lazy(() => import('user/activation/UserActivation'))
const UserActivationResend = React.lazy(() => import('user/activation/UserActivationResend'))
const UserPasswordForgot = React.lazy(() => import('user/password/forgot/UserPasswordForgot'))
const UserPasswordReset = React.lazy(() => import('user/password/reset/UserPasswordReset'))
const UserUsernameForgot = React.lazy(() => import('user/username/forgot/UserUsernameForgot'))
const UserUsernameGet = React.lazy(() => import('user/username/get/UserUsernameGet'))
const NurseDashboard = React.lazy(() => import('components/nurseDashboard/nurseDashboard'))
const NewFirstUser = React.lazy(() => import('user/NewFirstUser'))
const PrivacyPolicy = React.lazy(() => import('privacy/PrivacyPolicy'))
const AgreementSettings = React.lazy(() => import('settings/agreements/AgreementSettings'))
const VersionVerif = React.lazy(() => import('VersionVerif'))
const EndShiftChecker = React.lazy(() => import('user/endShift/EndShiftChecker'))
const ConfigurationMessageSettings = React.lazy(() =>
  import('configuration/configurationMessage/ConfigurationMessageSettings'),
)
const VdgPatients = React.lazy(() => import('vdg-patients/VdgPatients'))
const BillingReport = React.lazy(() => import('billingReport/BillingReport'))
const Reports = React.lazy(() => import('reports/Reports'))
const SupplyChecklistSettings = React.lazy(() =>
  import('settings/supplyChecklist/SupplyChecklistSettings'),
)
const ActionReport = React.lazy(() => import('vdg-patients/action-report/ActionReport'))
const PublicResult = React.lazy(() => import('public/result/PublicResult'))
const BotSymptomsTable = React.lazy(() => import('bot/tableOfSymptoms/index'))
const ConfigurationWorkingHours = React.lazy(() =>
  import('configuration/configurationWorkingHours/ConfigurationWorkingHours'),
)
const VdgPatientsListDashboard = React.lazy(() =>
  import('vdg-patients/patients/VdgPatientsListDashboard'),
)
const Sidebar = React.lazy(() => import('./components/sidebar/Sidebar'))
// const Dashboard = React.lazy(() => import('./dashboard/Dashboard'))
const Patients = React.lazy(() => import('./patients/list/Patients'))
const InterpreterWorklist = React.lazy(() => import('./interpreter/InterpreterWorklist'))

/**
 * A session that is authenticated but held behind one of the post-login gates.
 *
 * Both gates leave the app in the same state — the server refuses every protected
 * route — so the chrome is hidden for the same reason in both cases.
 */
const isGated = (userSession: any) =>
  !!userSession.facilitySelectionRequired || !!userSession.agreementRequired

const HospitalRun = () => {
  const sidebarCollapsed = useSelector((state: RootState) => state.components.sidebarCollapsed)
  const userSession = useSelector((state: RootState) => state.userSession.userSession)

  return (
    <div>
      <ForceUpdate />
      <VersionVerif />
      {/* Hidden while the facility gate is up: the counters are scoped to a facility
          that has not been chosen yet, and the server refuses the dashboard call
          behind the same gate, so rendering it only produces an empty bar and a
          failed request. The agreement gate is the same case — every route behind
          it answers 403 — and a navbar offering somewhere to go would also make
          the gate look optional when it is not. */}
      {userSession.ok && !isGated(userSession) && <Navbar />}
      {userSession.ok &&
        !isGated(userSession) &&
        shiftEnabled &&
        customerTypeConfig.isNursingHome() && <EndShiftChecker />}
      <div className="container-fluid">
        {!isInterpreter(userSession) && <Sidebar />}

        <ButtonBarProvider>
          <div className="row flex" style={{ background: '#f5f6f8' }}>
            <main
              style={{
                paddingRight: 0,
                paddingLeft: 0,
                width: '100%',
              }}
              role="main"
              /* The sidebar renders only for users who can reach a report, and it is a fixed-width
                 rail (see .sidebar in index.scss) rather than a bootstrap column — so main offsets
                 itself by that same width instead of guessing at a matching column span. */
              className={
                userSession.ok && canViewReports(userSession) && !isInterpreter(userSession)
                  ? `main-with-sidebar${
                      sidebarCollapsed ? ' main-with-sidebar--collapsed' : ''
                    } px-50`
                  : userSession.ok
                  ? 'col-md-12 col-lg-12 ml-sm-auto px-50'
                  : ''
              }
            >
              {!isInterpreter(userSession) && <Breadcrumbs />}

              {userSession.ok && (
                <div
                  className={`d-flex justify-content-start flex-wrap flex-md-nowrap align-items-end px-5 bg-white`}
                >
                  <ButtonToolBar />
                </div>
              )}
              <div style={{ background: '#F5F6F8' }}>
                <Suspense
                  fallback={<Spinner color="blue" loading size={[10, 25]} type="ScaleLoader" />}
                >
                  <Switch>
                    <Route exact path="/login" component={Login} />
                    <Route exact path="/login/mfa" component={Login} />
                    <Route exact path="/login/password/change-force" component={Login} />
                    <Route exact path="/first-user" component={NewFirstUser} />
                    <Route
                      exact
                      path="/user/activation/:userId&:registrationGuid"
                      component={UserActivation}
                    />
                    <Route exact path="/password-forgotten" component={UserPasswordForgot} />
                    <Route exact path="/username-forgotten" component={UserUsernameForgot} />
                    <Route exact path="/activation-resend" component={UserActivationResend} />
                    <Route
                      exact
                      path="/user/password/reset/:userId&:forgotPasswordGuid"
                      component={UserPasswordReset}
                    />
                    <Route
                      exact
                      path="/user/username/get/:emailEncrypt&:forgotUsernameGuid"
                      component={UserUsernameGet}
                    />
                    {/* One result, opened from the link on a PointClickCare report.
                        Public on purpose: the reader is usually a clinician at the
                        facility with no account here, which is why the old
                        login-gated link went unused. The token is the entitlement,
                        the page shows nothing but this result, and access is
                        audited whether or not a session happens to exist. */}
                    <Route exact path="/r/:token" component={PublicResult} />
                    {/* Must precede the "/" route below: Switch takes the first match, and a
                        supply manager has no patient access, so the patients dashboard the app
                        normally lands on would answer them with "No Permission". Covers both
                        the post-login push and anyone typing the bare URL. */}
                    {isSupplyManager(userSession) && (
                      <Redirect exact from="/" to="/supply-checklist" />
                    )}
                    {/* Same reason: an interpreter lands on the studies waiting for them. */}
                    {isInterpreter(userSession) && <Redirect exact from="/" to="/interpreter" />}
                    {/* Always registered, and private: on a reload the role isn't known yet, and
                        PrivateRoute is what restores the session before deciding. */}
                    <PrivateRoute
                      isAllowed={isInterpreter}
                      exact
                      path="/interpreter"
                      component={InterpreterWorklist}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/"
                      component={VdgPatientsListDashboard}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadSettings}
                      exact
                      path="/settings"
                      component={Settings}
                    />
                    {/* Every signed-in user edits their own details, whatever their
                        role — so no permission gate (ReadSettings would shut out
                        managers, medTechs and supply managers). ehr-server limits
                        what may be changed. */}
                    <PrivateRoute
                      isAllowed={() => true}
                      exact
                      path="/profile"
                      component={MyProfile}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadSettings}
                      exact
                      path="/privacy"
                      component={PrivacyPolicy}
                    />
                    {/* Publishing the End User Agreement. AdminPermissions gates the
                        screen; ehr-server asserts session.admin on the routes behind
                        it, and confines a non-superAdmin to their own organisation. */}
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/settings/agreements"
                      component={AgreementSettings}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadSettings}
                      exact
                      path="/nursedashboard"
                      component={NurseDashboard}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/patients"
                      component={Patients}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/vdg-patients"
                      component={VdgPatientsListDashboard}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.WritePatients}
                      exact
                      path="/patients/new"
                      component={NewPatient}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.WritePatients}
                      exact
                      path="/patients/edit/:id"
                      component={EditPatient}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/patients/:patientId/referral/new"
                      component={NewReferral}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/patients/:patientId/approval/new"
                      component={NewApproval}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/patients/:id"
                      component={ViewPatient}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/vdg-patients/:id"
                      component={VdgPatients}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/users"
                      component={Users}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/users/new"
                      component={NewUser}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/users/edit/:id"
                      component={EditUser}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      path="/users/:id"
                      component={ViewUser}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/groups"
                      component={Groups}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/device-incidents"
                      component={DeviceIncidents}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/groups/new"
                      component={NewGroup}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      exact
                      path="/groups/edit/:id"
                      component={EditGroup}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.AdminPermissions}
                      path="/groups/:id"
                      component={ViewGroupTabs}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadAppointments}
                      exact
                      path="/appointments"
                      component={Appointments}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadAppointments}
                      exact
                      path="/appointments/:id"
                      component={ViewAppointment}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadAppointments}
                      exact
                      path="/appointments/category/:category"
                      component={Appointments}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/clinics"
                      component={Clinics}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/configuration"
                      component={ConfigurationMessageSettings}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/billingreport"
                      component={BillingReport}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      isAllowed={canViewReports}
                      path="/reports"
                      component={Reports}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      isAllowed={canManageSupplyChecklist}
                      path="/supply-checklist"
                      component={SupplyChecklistSettings}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/action-report"
                      component={ActionReport}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      exact
                      path="/tableOfSymptoms"
                      component={BotSymptomsTable}
                    />
                    <PrivateRoute
                      neededPermissions={Permissions.ReadPatients}
                      path="/workinghours"
                      component={ConfigurationWorkingHours}
                    />
                  </Switch>
                </Suspense>
              </div>

              <Toaster autoClose={5000} hideProgressBar draggable />
            </main>
          </div>
        </ButtonBarProvider>
      </div>
    </div>
  )
}

export default HospitalRun
